import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { X, ChevronLeft, ChevronRight, Heart, Shield, Camera, Users, Play } from 'lucide-react';

// Facility photos and videos for the album
const facilityMedia = [
  {
    url: "https://customer-assets.emergentagent.com/job_88aa9ec7-4e33-45f5-9f03-fcaf81350df6/artifacts/z9lmnbd1_IMG_4740.jpeg",
    alt: "Beautiful pond on the property",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/740rwrqj_IMG_2159.jpeg",
    alt: "Building progress",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/1vmf88e3_IMG_9604.jpeg",
    alt: "Facility development",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/lnzw6cye_IMG_2517.jpeg",
    alt: "Construction milestone",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/nqx3bot3_IMG_4589.jpeg",
    alt: "Property development",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/1bozda51_IMG_4738.mov",
    alt: "Property video tour",
    type: "video"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/y4tpsgfk_IMG_4119.jpeg",
    alt: "Property landscape",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/5f2wbboj_IMG_7712.jpeg",
    alt: "Build completion",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_88aa9ec7-4e33-45f5-9f03-fcaf81350df6/artifacts/zs5ipe7d_FB9091F9-7128-4D07-8452-D33769EA1383.jpeg",
    alt: "Future site construction",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/vohp398f_IMG_2291.jpeg",
    alt: "Property view",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_88aa9ec7-4e33-45f5-9f03-fcaf81350df6/artifacts/sm2jhd9w_IMG_4755.jpeg",
    alt: "Site preparation",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_88aa9ec7-4e33-45f5-9f03-fcaf81350df6/artifacts/be3lc6zw_IMG_4864.jpeg",
    alt: "Facility construction progress",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_88aa9ec7-4e33-45f5-9f03-fcaf81350df6/artifacts/inki77jl_IMG_4996.jpeg",
    alt: "Play area preparation",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/vr3c4hcv_IMG_4626.jpeg",
    alt: "Construction work",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/jkpqsz76_IMG_4991.jpeg",
    alt: "Build update",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/olc06yw5_IMG_5291.jpeg",
    alt: "Facility exterior",
    type: "image"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/jf61tf0k_IMG_5293.jpeg",
    alt: "Facility progress",
    type: "image"
  }
];

const Facility = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % facilityMedia.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + facilityMedia.length) % facilityMedia.length);
  };

  const currentMedia = facilityMedia[currentPhotoIndex];

  return (
    <div className="min-h-screen bg-background">
      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
          >
            <X className="h-8 w-8" />
          </button>
          
          <button 
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            className="absolute left-4 text-white hover:text-gray-300 z-50"
          >
            <ChevronLeft className="h-12 w-12" />
          </button>
          
          {currentMedia.type === "video" ? (
            <video
              src={currentMedia.url}
              controls
              autoPlay
              className="max-h-[85vh] max-w-[90vw]"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={currentMedia.url}
              alt={currentMedia.alt}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          
          <button 
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            className="absolute right-4 text-white hover:text-gray-300 z-50"
          >
            <ChevronRight className="h-12 w-12" />
          </button>
          
          <div className="absolute bottom-4 text-white text-center">
            <p className="text-sm">{currentMedia.alt}</p>
            <p className="text-xs text-gray-400 mt-1">{currentPhotoIndex + 1} of {facilityMedia.length}</p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-secondary/30 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Our Facility
            </h1>
            <p className="text-xl text-muted-foreground">
              A paradise for dogs nestled in the beautiful mountains of Mission, BC
            </p>
          </div>
        </div>
      </section>

      {/* Facility Description */}
      <section className="section-padding">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6 text-muted-foreground mb-12">
              <p className="text-lg text-center">
                Our facility in Stave Falls is nestled on nearly four acres surrounded by mountains, sunshine, and creeks — a true paradise for dogs to explore, play, and relax.
              </p>
            </div>
            
            {/* The Property and Build Sub-section */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
                The Property & Build
              </h2>
              <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
                Follow along as we transform this beautiful property into the ultimate dog boarding destination.
              </p>
              <p className="text-center text-sm text-muted-foreground mb-6">
                Click any photo or video to view the album
              </p>
              <div className="grid grid-cols-4 md:grid-cols-7 lg:grid-cols-13 gap-3">
                {facilityMedia.map((media, index) => (
                  <div 
                    key={index}
                    onClick={() => openLightbox(index)}
                    className="rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer hover:scale-105 relative"
                  >
                    {media.type === "video" ? (
                      <>
                        <video
                          src={media.url}
                          className="w-full h-24 object-cover"
                          muted
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play className="h-8 w-8 text-white fill-white" />
                        </div>
                      </>
                    ) : (
                      <img
                        src={media.url}
                        alt={media.alt}
                        className="w-full h-24 object-cover"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="section-padding bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
              Our Mission & Values
            </h2>
            <div className="space-y-6">
              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Heart className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-2">Personalized 1:1 Care</h3>
                      <p className="text-muted-foreground">
                        We specialize in providing individualized attention for every dog. No crowded kennels — just genuine, loving care tailored to your dog's unique personality and needs.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Shield className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-2">Safe & Calm Environment</h3>
                      <p className="text-muted-foreground">
                        Our facility is designed for dogs who may feel overwhelmed in traditional boarding environments. We provide a peaceful, home-like atmosphere where anxious dogs can feel safe.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Camera className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-2">Regular Updates</h3>
                      <p className="text-muted-foreground">
                        Stay connected with your pet through regular photo and video updates during their stay. See them enjoying their time while you're away.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Users className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-2">Understanding Misunderstood Breeds</h3>
                      <p className="text-muted-foreground">
                        We have a deep love for pitbull-type breeds and all misunderstood dogs. Here, every dog is seen for who they truly are — not judged by their appearance.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
              What Sets Us Apart
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="hover-lift">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-2">Home Away From Home</h3>
                  <p className="text-muted-foreground">
                    Your dog stays in our home, not a kennel. They become part of our family during their visit.
                  </p>
                </CardContent>
              </Card>
              <Card className="hover-lift">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-2">Nature & Exploration</h3>
                  <p className="text-muted-foreground">
                    Nearly four acres of mountains, creeks, and open space for your dog to explore and enjoy.
                  </p>
                </CardContent>
              </Card>
              <Card className="hover-lift">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-2">Customized Care Plans</h3>
                  <p className="text-muted-foreground">
                    We tailor our services to meet each dog's unique needs, from dietary requirements to exercise routines.
                  </p>
                </CardContent>
              </Card>
              <Card className="hover-lift">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-2">Love, Structure & Joy</h3>
                  <p className="text-muted-foreground">
                    Every dog is treated like family — with consistent routines, plenty of affection, and lots of fun.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Facility;
